import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, PlayCircle, Star, ShieldCheck, ShoppingBag, Copy, Send, Coins, Eye, Heart, Share2, MousePointerClick, BadgeCheck, BookOpen, Target, Link as LinkIcon, BarChart3, TrendingUp, Crown } from "lucide-react";
import { useLang } from "@/i18n";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";

export const IMG = {
  hero: "/mk-hero-actor.jpg",
  split: "/mk-spectator-actor.jpg",
  community: [
    "https://images.unsplash.com/photo-1758272133771-b149318883c5?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    "https://images.pexels.com/photos/8199178/pexels-photo-8199178.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    "https://images.unsplash.com/photo-1653762379954-8943c787e78b?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  ],
  mockup: "https://images.unsplash.com/photo-1695973056909-67189edc1c9e?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  journey: [
    "https://static.prod-images.emergentagent.com/jobs/154d3f06-c9f0-40cc-b696-0eaf1b76eea4/images/8e8e13a4aa59e5c456b40b4ed57e3c79bd508c7e00eef60cc02e632192a3355f.jpeg",
    "https://static.prod-images.emergentagent.com/jobs/154d3f06-c9f0-40cc-b696-0eaf1b76eea4/images/2b574c103e9121d8c2f33103d5496eb0bd7ce43792a3ccd96bfc328c2ca190b6.jpeg",
    "https://static.prod-images.emergentagent.com/jobs/154d3f06-c9f0-40cc-b696-0eaf1b76eea4/images/6c8c32edf1eacb8ab86ef8ed63ff6409adf89b59f13f82e75dc0a72377a166be.jpeg",
    "https://static.prod-images.emergentagent.com/jobs/154d3f06-c9f0-40cc-b696-0eaf1b76eea4/images/475bfc66a6b29476e1e7bf1ad3a36044f70edffec1152b6fc5ece31d2d79d3b8.jpeg",
    "https://static.prod-images.emergentagent.com/jobs/154d3f06-c9f0-40cc-b696-0eaf1b76eea4/images/313062a7918980204678c21dc02ed59ddf621956957bd63ef648929903f4c2aa.jpeg",
  ],
};

export function DarkPage({ children, className = "" }) {
  return (
    <div className={`relative min-h-screen bg-[#050505] text-white overflow-hidden ${className}`}>
      <div className="pointer-events-none absolute inset-0 bt-noise opacity-100" />
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-[#7C3AED]/15 blur-3xl" />
      <div className="relative">{children}</div>
    </div>
  );
}

export function Reveal({ children, delay = 0, className = "", ...rest }) {
  return (
    <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }} className={className} {...rest}>
      {children}
    </motion.div>
  );
}

export function SectionKicker({ children }) {
  return <div className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em] text-violet-300/80 mb-4"><span className="w-6 h-px bg-violet-400/60" />{children}</div>;
}

export function WakeMarquee({ lines }) {
  const doubled = [...lines, ...lines];
  return (
    <div className="bt-marquee-wrap relative py-10 border-y border-white/10 overflow-hidden" data-testid="wake-marquee">
      <div className="bt-marquee-track gap-10">
        {doubled.map((l, i) => (
          <span key={i} className="font-head font-black text-4xl sm:text-6xl lg:text-7xl bt-stroke uppercase shrink-0">{l} <span className="text-violet-500 mx-6">•</span></span>
        ))}
      </div>
    </div>
  );
}

const SPEC_ICONS = [Eye, Heart, Share2, MousePointerClick, Share2];
const ACT_ICONS = [ShoppingBag, Copy, Send, Send, Coins, BadgeCheck];

export function SplitSpectatorActor() {
  const { t } = useLang();
  return (
    <div className="grid md:grid-cols-2 gap-6 group" data-testid="split-section">
      {/* Spectator */}
      <Reveal className="relative rounded-3xl overflow-hidden border border-white/10 bg-[#0e0e10] p-8 md:p-10 transition-opacity group-hover:opacity-70 hover:!opacity-100" >
        <div className="text-zinc-500 text-sm font-black uppercase tracking-widest mb-6">{t("spectator_label")}</div>
        <ul className="space-y-4">
          {["spec_1", "spec_2", "spec_3", "spec_4", "spec_5"].map((k, i) => {
            const Icon = SPEC_ICONS[i];
            return <li key={k} className="flex items-center gap-3 text-zinc-400"><span className="grid place-items-center w-9 h-9 rounded-xl bg-white/5 text-zinc-500"><Icon size={16} /></span>{t(k)}</li>;
          })}
        </ul>
      </Reveal>
      {/* Actor */}
      <Reveal delay={0.1} className="relative rounded-3xl overflow-hidden border border-violet-500/40 bg-gradient-to-b from-violet-600/15 to-transparent p-8 md:p-10 transition-opacity group-hover:opacity-70 hover:!opacity-100">
        <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-violet-600/25 blur-3xl" />
        <div className="relative">
          <div className="text-violet-300 text-sm font-black uppercase tracking-widest mb-6">{t("actor_label")}</div>
          <ul className="space-y-4">
            {["act_1", "act_2", "act_3", "act_4", "act_5", "act_6"].map((k, i) => {
              const Icon = ACT_ICONS[i];
              return <li key={k} className="flex items-center gap-3 text-white font-medium"><span className="grid place-items-center w-9 h-9 rounded-xl bt-gradient text-white"><Icon size={16} /></span>{t(k)}</li>;
            })}
          </ul>
        </div>
      </Reveal>
    </div>
  );
}

export function ReadyToPublish() {
  const { t } = useLang();
  const cards = [
    { icon: PlayCircle, t: "ready2_v", d: "ready2_vd" },
    { icon: Copy, t: "ready2_t", d: "ready2_td" },
    { icon: Send, t: "ready2_l", d: "ready2_ld" },
  ];
  return (
    <div className="grid md:grid-cols-3 gap-6" data-testid="ready-to-publish">
      {cards.map((c, i) => (
        <Reveal key={i} delay={i * 0.08} className="relative rounded-3xl border border-white/10 bg-gradient-to-b from-white/5 to-transparent p-7 hover:-translate-y-1 transition-transform">
          <span className="grid place-items-center w-12 h-12 rounded-2xl bg-violet-500/15 text-violet-300 mb-5"><c.icon size={22} /></span>
          <h3 className="font-head font-bold text-xl mb-2">{t(c.t)}</h3>
          <p className="text-zinc-400 text-sm leading-relaxed">{t(c.d)}</p>
          <Check className="absolute top-6 right-6 text-violet-400" size={18} />
        </Reveal>
      ))}
    </div>
  );
}

const GUIDE_ICONS = [Target, Send, LinkIcon, BarChart3, TrendingUp, Crown];

export function TutorialCard({ tut, index }) {
  const { t } = useLang();
  const Icon = GUIDE_ICONS[index % GUIDE_ICONS.length];
  return (
    <Reveal delay={(index % 3) * 0.06} className="group rounded-3xl border border-white/10 bg-white/5 overflow-hidden hover:-translate-y-1 transition-transform" data-testid={`tutorial-card-${index}`}>
      <div className="relative aspect-video overflow-hidden bg-gradient-to-br from-violet-600/25 via-[#141033] to-[#0a0a0f]">
        <div className="pointer-events-none absolute -top-10 -right-8 w-40 h-40 rounded-full bg-violet-500/20 blur-3xl" />
        <div className="absolute inset-0 grid place-items-center">
          <span className="grid place-items-center w-16 h-16 rounded-2xl bg-white/10 border border-white/15 text-violet-200 group-hover:scale-110 transition-transform"><Icon size={28} /></span>
        </div>
        <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur border border-white/25 text-white text-[11px] font-bold px-2.5 py-1 uppercase tracking-wide"><BookOpen size={13} /> {t("academy_guide_badge")}</span>
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 text-xs font-black text-violet-300 mb-1">{String(index + 1).padStart(2, "0")}</div>
        <h3 className="font-head font-bold text-lg mb-1">{tut.title}</h3>
        <p className="text-zinc-400 text-sm">{tut.desc}</p>
        <div className="mt-3 h-1 rounded-full bg-white/10"><div className="h-1 rounded-full bt-gradient" style={{ width: "0%" }} /></div>
      </div>
    </Reveal>
  );
}

export function TutorialGrid({ tutorials }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="tutorial-grid">
      {(tutorials || []).map((tut, i) => <TutorialCard key={i} tut={tut} index={i} />)}
    </div>
  );
}

export function TestimonialCard({ item }) {
  const { t } = useLang();
  const initial = (item.name || "?").charAt(0).toUpperCase();
  return (
    <div className="break-inside-avoid mb-6 rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl p-6" data-testid="testimonial-card">
      <div className="flex items-center gap-3 mb-4">
        {item.avatar ? <img src={item.avatar} alt={item.name} className="w-11 h-11 rounded-full object-cover" /> :
          <span className="grid place-items-center w-11 h-11 rounded-full bt-gradient text-white font-black">{initial}</span>}
        <div>
          <div className="flex items-center gap-1.5 font-bold">{item.name}{item.verified && <ShieldCheck size={14} className="text-violet-400" title={t("mk_verified")} />}</div>
          <div className="text-xs text-zinc-500">{item.country}{item.date ? ` · ${item.date}` : ""}</div>
        </div>
      </div>
      <div className="flex gap-0.5 mb-2 text-violet-400">{[0, 1, 2, 3, 4].map((s) => <Star key={s} size={13} fill="currentColor" />)}</div>
      <p className="text-zinc-300 leading-relaxed">“{item.comment}”</p>
    </div>
  );
}

export function TestimonialFeed({ testimonials }) {
  const { t } = useLang();
  return (
    <div>
      <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-300 text-xs font-bold px-3 py-1.5 mb-6" data-testid="feed-label">{t("feed2_label")}</div>
      <div className="columns-1 sm:columns-2 lg:columns-3 gap-6" data-testid="testimonial-feed">
        {(testimonials || []).map((item, i) => <TestimonialCard key={i} item={item} />)}
      </div>
      <p className="text-zinc-500 text-sm mt-4" data-testid="feed-disclaimer">{t("feed_disclaimer")}</p>
    </div>
  );
}

export function FaqList({ items, testid = "faq-list" }) {
  return (
    <Accordion type="single" collapsible className="w-full" data-testid={testid}>
      {(items || []).map((f, i) => (
        <AccordionItem key={i} value={`q${i}`} className="border-white/10">
          <AccordionTrigger className="text-left font-head font-bold text-lg text-white hover:text-violet-300 hover:no-underline py-5" data-testid={`faq-q-${i}`}>{f.q}</AccordionTrigger>
          <AccordionContent className="text-zinc-400 text-base leading-relaxed pb-5">{f.a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

export function HowBetoolWorks({ showCta = true }) {
  const { t } = useLang();
  const nav = useNavigate();
  const steps = [
    { img: IMG.journey[0], icon: ShoppingBag, t: "he_s1_t", d: "he_s1_d" },
    { img: IMG.journey[1], icon: Copy, t: "he_s2_t", d: "he_s2_d" },
    { img: IMG.journey[2], icon: Send, t: "he_s3_t", d: "he_s3_d" },
    { img: IMG.journey[3], icon: MousePointerClick, t: "he_s4_t", d: "he_s4_d" },
    { img: IMG.journey[4], icon: Coins, t: "he_s5_t", d: "he_s5_d" },
  ];
  return (
    <div data-testid="how-betool-works">
      <Reveal className="text-center max-w-2xl mx-auto mb-14">
        <h2 className="font-head font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-3">{t("he_title")}</h2>
        <p className="text-zinc-400 text-lg">{t("he_sub")}</p>
      </Reveal>

      <div className="relative">
        {/* central connecting spine (desktop) */}
        <div className="hidden md:block absolute left-1/2 top-4 bottom-4 w-px bg-gradient-to-b from-violet-500/0 via-violet-500/40 to-violet-500/0 -translate-x-1/2" />

        <div className="space-y-10 md:space-y-16">
          {steps.map((s, i) => {
            const reversed = i % 2 === 1;
            return (
              <Reveal key={i} delay={i * 0.08} data-testid={`he-step-${i}`} className="relative">
                <div className="grid md:grid-cols-2 gap-6 md:gap-14 items-center">
                  {/* Visual */}
                  <div className={reversed ? "md:order-2" : "md:order-1"}>
                    <div className="relative">
                      <div className="pointer-events-none absolute -inset-4 bg-violet-600/15 blur-3xl rounded-[2rem]" />
                      <div className="relative rounded-[1.75rem] overflow-hidden border border-white/10 bg-white/5 shadow-2xl shadow-violet-950/50">
                        <img src={s.img} alt={t(s.t)} loading="lazy" className="w-full aspect-[3/2] object-cover object-center" />
                      </div>
                    </div>
                  </div>
                  {/* Text */}
                  <div className={reversed ? "md:order-1 md:text-right" : "md:order-2"}>
                    <div className={`flex items-center gap-4 mb-4 ${reversed ? "md:flex-row-reverse" : ""}`}>
                      <span className="grid place-items-center w-14 h-14 rounded-2xl bt-gradient text-white shadow-lg shadow-violet-900/40 shrink-0"><s.icon size={24} /></span>
                      <span className="font-head font-black text-5xl sm:text-6xl bt-gradient-text leading-none">{i + 1}</span>
                    </div>
                    <h3 className="font-head font-bold text-2xl sm:text-3xl mb-3 leading-tight">{t(s.t)}</h3>
                    <p className={`text-zinc-400 text-base sm:text-lg leading-relaxed ${reversed ? "md:ml-auto" : ""} max-w-md`}>{t(s.d)}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>

      {showCta && (
        <div className="text-center mt-16">
          <Button onClick={() => nav("/auth?mode=signup")} data-testid="he-cta" className="rounded-full bt-gradient text-white hover:opacity-90 text-lg font-bold px-9 min-h-[60px]">{t("he_cta")} <ArrowRightMk /></Button>
          <p className="text-zinc-500 text-sm mt-4">{t("he_secondary")}</p>
        </div>
      )}
    </div>
  );
}

function ArrowRightMk() { return <svg className="inline ml-2" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7" /></svg>; }

export function FinalCta({ content }) {
  const { t } = useLang();
  const nav = useNavigate();
  const fc = content?.final_cta || {};
  return (
    <section className="relative py-24 md:py-36">
      <div className="pointer-events-none absolute inset-0 grid place-items-center"><div className="w-[700px] h-[500px] rounded-full bg-violet-600/25 blur-3xl" /></div>
      <div className="relative max-w-4xl mx-auto px-6 text-center">
        <Reveal>
          <h2 className="font-head font-black text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight mb-6">{fc.title}</h2>
          <p className="text-zinc-300 text-lg max-w-2xl mx-auto mb-9">{fc.sub}</p>
          <Button onClick={() => nav("/auth?mode=signup")} data-testid="final-cta-btn" className="rounded-full bt-gradient text-white hover:opacity-90 text-lg font-bold px-9 min-h-[64px]">
            {fc.cta} <Send size={18} className="ml-2" />
          </Button>
          <div className="mt-10 font-head font-black text-2xl sm:text-3xl bt-gradient-text">{t("final_sig")}</div>
        </Reveal>
      </div>
    </section>
  );
}
